import type { BugReportListItem, RepeatBadgeInfo, RepeatView } from '../bugApi';

// Пов13 (model quint/specs/repeat_signals_flags.qnt, badgeFollowsDecision): a decision made on the open card reaches the open list
// or board at once, so the badge changes without reloading the list.

/** The badge of a report after a decision — the same rule the service uses for the list: a confirmed link, else a high suggestion. */
export function badgeOfView(view: RepeatView): RepeatBadgeInfo | undefined {
  const link = view.links.find((l) => l.kind === 'repeat' || l.kind === 'continues');
  if (link) return { kind: link.kind as 'repeat' | 'continues', number: link.number, direction: link.direction };
  const high = view.suggestions.find((s) => s.confidence === 'high');
  return high ? { kind: 'suggest', number: high.number, direction: 'earlier' } : undefined;
}

function withBadge(report: BugReportListItem, repeat: RepeatBadgeInfo | undefined): BugReportListItem {
  const { repeat: _old, ...rest } = report;
  return repeat ? { ...rest, repeat } : rest;
}

/** The list after a decision on `reportId`: its own badge and the badge of the other end of a confirmed pair. */
export function applyDecision(reports: BugReportListItem[], reportId: number, view: RepeatView): BugReportListItem[] {
  const me = reports.find((r) => r.id === reportId);
  if (!me) return reports;
  const myNumber = me.number ?? me.id;
  return reports.map((r) => {
    if (r.id === reportId) return withBadge(r, badgeOfView(view));
    const confirmed = view.links.find((l) => (l.kind === 'repeat' || l.kind === 'continues') && l.number === (r.number ?? r.id));
    if (confirmed) {
      return withBadge(r, { kind: confirmed.kind as 'repeat' | 'continues', number: myNumber, direction: confirmed.direction === 'earlier' ? 'later' : 'earlier' });
    }
    // the other end showed this pair; the decision was taken back
    if (r.repeat && r.repeat.kind !== 'suggest' && r.repeat.number === myNumber) return withBadge(r, undefined);
    return r;
  });
}

type Listener = (reportId: number, view: RepeatView) => void;
const listeners = new Set<Listener>();

export function onRepeatDecided(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function emitRepeatDecided(reportId: number, view: RepeatView): void {
  for (const listener of [...listeners]) listener(reportId, view);
}
